import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

// Tarjeta genérica que aparece bajo el chat cuando el asistente necesita un dato con opciones.
// Primer uso: el valor de la hora. Reutilizable para otros campos (tipo "opciones").
export default function TarjetaChat({ tarjeta, onGuardarValor, onOpcion }) {
  const [modo, setModo] = useState("menu");
  const [numero, setNumero] = useState("");
  const [ganado, setGanado] = useState("");
  const [horasSem, setHorasSem] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [confirm, setConfirm] = useState(null); // {vh, origen, mensaje} cuando el valor es extremo

  if (!tarjeta) return null;

  const guardar = async (vh, origen, confirmado = false) => {
    if (!vh || vh < 1) return;
    setGuardando(true);
    try {
      const data = await onGuardarValor?.(Math.round(vh), origen, confirmado);
      if (data?.needs_confirm) setConfirm({ vh: data.valor_hora ?? Math.round(vh), origen, mensaje: data.mensaje });
      else setConfirm(null);
    } finally { setGuardando(false); }
  };

  const horasMes = () => Math.round((parseFloat(horasSem) || 0) * 4.33);
  const estimado = () => {
    const r = parseFloat(ganado), hm = horasMes();
    if (!r || !hm) return 0;
    return Math.round(r / hm);
  };

  const wrap = (children) => (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="card-dark p-4 mt-3" style={{ borderColor: "rgba(201,168,76,0.4)" }} data-testid="tarjeta-chat">
      {children}
    </motion.div>
  );

  // ---- Tarjeta genérica de opciones (para reusar) ----
  if (tarjeta.tipo === "opciones") {
    return wrap(
      <div className="flex flex-wrap gap-2" data-testid={`tarjeta-opciones-${tarjeta.campo}`}>
        {(tarjeta.opciones || []).map((o) => (
          <button key={o.v} onClick={() => onOpcion?.(tarjeta.campo, o.v)} data-testid={`tarjeta-opcion-${o.v}`}
            className="btn-fantasma">{o.label}</button>
        ))}
      </div>
    );
  }

  // ---- Tarjeta específica: valor de la hora ----
  if (tarjeta.tipo === "valor_hora") {
    if (confirm) {
      return wrap(
        <div data-testid="tarjeta-vh-confirmar-extremo">
          <p className="text-crema text-sm leading-relaxed mb-4">{confirm.mensaje}</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => guardar(confirm.vh, confirm.origen, true)} disabled={guardando}
              className="btn-dorado flex items-center gap-2" data-testid="vh-confirm-si">
              <Check size={14} /> {guardando ? "…" : "está bien"}
            </button>
            <button onClick={() => { setConfirm(null); setModo("numero"); }} className="btn-fantasma" data-testid="vh-confirm-corrijo">
              lo corrijo
            </button>
          </div>
        </div>
      );
    }
    if (modo === "menu") {
      return wrap(
        <div data-testid="tarjeta-valor-hora">
          <div className="text-crema text-sm leading-relaxed space-y-2 mb-4">
            <p>Para ponerle un número en plata a todo esto necesito algo tuyo, y te lo explico antes de pedírtelo.</p>
            <p>Hasta acá tenemos horas. Las horas se entienden, pero no mueven a nadie. Cuando esas horas se convierten en plata es cuando se ve de verdad qué está pasando y por qué vale la pena arreglarlo.</p>
            <p className="text-gris">No necesito el número exacto ni te lo va a pedir nadie más. Es un estimado tuyo, y lo podés cambiar cuando quieras: todos los números se recalculan solos.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setModo("numero")} className="btn-dorado" data-testid="vh-tengo-numero">Ya sé cuánto vale mi hora</button>
            <button onClick={() => setModo("estimar")} className="btn-fantasma" data-testid="vh-estimar">Estimémoslo juntos</button>
          </div>
        </div>
      );
    }
    if (modo === "numero") {
      return wrap(
        <div data-testid="tarjeta-vh-numero">
          <div className="mono-label text-gris mb-2">TU HORA (USD)</div>
          <div className="flex items-center gap-2">
            <div className="flex items-center card-dark px-3 py-2 gap-2 flex-1" style={{ background: "rgba(255,255,255,0.03)" }}>
              <span className="mono-label text-gris">USD</span>
              <input type="number" min="1" value={numero} autoFocus
                onChange={(e) => setNumero(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && guardar(parseInt(numero, 10), "dicho")}
                className="bg-transparent text-crema outline-none w-full text-lg" data-testid="vh-input" />
              <span className="mono-label text-gris">/ HORA</span>
            </div>
            <button onClick={() => guardar(parseInt(numero, 10), "dicho")} disabled={guardando}
              className="btn-dorado flex items-center gap-2" data-testid="vh-guardar">
              <Check size={14} /> {guardando ? "…" : "LISTO"}
            </button>
          </div>
        </div>
      );
    }
    // estimar (pregunta híbrida: sirve para dueño y para empleado)
    const vh = estimado();
    const hm = horasMes();
    const gNum = parseFloat(ganado) || 0;
    return wrap(
      <div data-testid="tarjeta-vh-estimar">
        <p className="text-crema text-sm mb-3">Con dos datos lo sacamos:</p>
        <div className="space-y-3 mb-3">
          <div>
            <div className="flex items-center card-dark px-3 py-2 gap-2" style={{ background: "rgba(255,255,255,0.03)" }}>
              <span className="mono-label text-gris w-28 shrink-0">GANÁS / MES</span>
              <span className="mono-label text-gris">USD</span>
              <input type="number" min="0" value={ganado} onChange={(e) => setGanado(e.target.value)}
                className="bg-transparent text-crema outline-none w-full" data-testid="vh-ganado" placeholder="1200" />
            </div>
            <p className="text-gris text-xs mt-1.5 ml-1" data-testid="vh-ganado-ayuda">Si te varía mucho mes a mes, poné un promedio de los últimos seis.</p>
          </div>
          <div className="flex items-center card-dark px-3 py-2 gap-2" style={{ background: "rgba(255,255,255,0.03)" }}>
            <span className="mono-label text-gris w-28 shrink-0">HORAS / SEMANA</span>
            <input type="number" min="0" value={horasSem} onChange={(e) => setHorasSem(e.target.value)}
              className="bg-transparent text-crema outline-none w-full" data-testid="vh-horas" placeholder="45" />
          </div>
        </div>
        {vh > 0 && (
          <div className="flex items-center justify-between gap-2">
            <p className="text-crema text-sm" data-testid="vh-estimado">
              Entonces tu hora vale alrededor de <span className="text-dorado font-medium">USD {vh}</span>{" "}
              <span className="text-gris">({gNum.toLocaleString("es-AR")} por mes ÷ {hm} horas)</span>. ¿Te cierra o lo ajustamos?
            </p>
            <button onClick={() => guardar(vh, "estimado")} disabled={guardando} className="btn-dorado flex items-center gap-2 shrink-0" data-testid="vh-confirmar">
              <Check size={14} /> {guardando ? "…" : "SÍ, USÁ ESE"}
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
}
